// src/lib/structured-builder/widget-types/product-collection.ts
//
// Config type and defaults for the Product Collection widget.

/**
 * Media shape for the card image. `rectangle` is 3:4 portrait — the ratio the
 * /products listing grid already uses, so a builder-placed collection and the
 * listing frame their photos identically.
 */
export type CardShape = 'rectangle' | 'square';

export const CARD_SHAPE_ASPECT: Record<CardShape, string> = {
  rectangle: 'aspect-[3/4]',
  square: 'aspect-square',
};

export const CARD_SHAPES: { key: CardShape; label: string; desc: string }[] = [
  { key: 'rectangle', label: 'Rectangle', desc: 'Portrait 3:4 — matches the products listing' },
  { key: 'square', label: 'Square', desc: '1:1 — shorter cards, more per row' },
];

export type CardVariant =
  | 'modern'
  | 'modern-cta'
  | 'rating'
  | 'minimal'
  | 'elegant'
  | 'dark';

export type ProductCollectionConfig = {
  _type: 'product_collection';
  /** Card design variant */
  card_variant: CardVariant;
  /** Card image shape. Absent on documents saved before this existed — every
   *  reader defaults it to 'rectangle'. */
  card_shape?: CardShape;
  /** Section heading */
  heading_ar?: string;
  heading_en?: string;
  /** Section subtitle */
  subtitle_ar?: string;
  subtitle_en?: string;
  /** Display as carousel or grid */
  layout: 'carousel' | 'grid';
  /** Number of products to show */
  limit: number;
  /** Source: collection ID, category ID, or manual product IDs */
  source: 'collection' | 'category' | 'manual' | 'latest' | 'bestseller' | 'brand';
  /** Collection ID when source=collection */
  collection_id?: string;
  /** Category ID when source=category */
  category_id?: string;
  /** Manual product IDs when source=manual */
  product_ids?: string[];
  /** Show wishlist button */
  show_wishlist: boolean;
  /** Show add to cart button */
  show_add_to_cart: boolean;
  /** Show category tag on cards */
  show_category_tag: boolean;
  /**
   * Show a "see all" link beside the heading, pointing at the collection or
   * category this list is drawn from. Absent on documents saved before this
   * existed — every reader defaults it to false, so existing pages are
   * unchanged. Meaningless for source='manual', which has no destination.
   */
  show_see_all?: boolean;
};

export const DEFAULT_PRODUCT_COLLECTION_CONFIG: ProductCollectionConfig = {
  _type: 'product_collection',
  card_variant: 'modern',
  card_shape: 'rectangle',
  heading_ar: 'منتجات مميزة',
  heading_en: 'Featured Products',
  subtitle_ar: '',
  subtitle_en: '',
  layout: 'carousel',
  limit: 8,
  source: 'latest',
  show_wishlist: true,
  show_add_to_cart: true,
  show_category_tag: true,
  show_see_all: false,
};

/** Variant metadata for the visual picker */
export const PRODUCT_CARD_VARIANTS: { key: CardVariant; label: string; desc: string }[] = [
  { key: 'modern', label: 'Modern', desc: 'Swatches + quick view button' },
  {
    key: 'modern-cta',
    label: 'Modern + Button',
    desc: 'Swatches + full-width add to cart',
  },
  { key: 'rating', label: 'Rating', desc: 'Heart, star rating, reviews' },
  { key: 'minimal', label: 'Minimal', desc: 'Clean image + name + price' },
  { key: 'elegant', label: 'Elegant', desc: 'Tall image, hover add to cart' },
  { key: 'dark', label: 'Framed', desc: 'Inset image, rating badge' },
];
